import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, formatPrice, type Channel, type Listing, type ProductRow } from '../lib/api';
import { PriceCell } from './PriceCell';
import type { DraftEdit } from './ProductTable';
import { businessPriceRuleLabel, deriveBusinessPrice, MIRROR_RETAIL, type BusinessPriceRule } from '@mfs/shared';

/**
 * The expanded "Product Data" section: one row per channel, aligned to the
 * master row's columns.
 *
 * These were cards in a wrapped panel, which meant comparing a field across
 * channels involved reading around a set of boxes rather than down a column.
 * As rows they share the grid the master row already establishes, so a
 * channel's price sits directly under that channel's own heading.
 *
 * Not every field has a column — category, quantity, whatever extra fields a
 * channel reports, the write history — so each channel row carries a second,
 * collapsed row holding the rest. Fields a channel cannot write are shown with
 * the reason, rather than failing at save time.
 */

/**
 * How many columns the master row has: Source / expand / thumb / Product /
 * SKU, one "listed?" column per marketplace, one price column per channel.
 * Shared rather than recomputed at each call site — this and the master row
 * itself (ProductTable.tsx) are the only two places that need to agree on
 * it, and a second inline copy is how they'd drift.
 */
export function tableColumnSpan(channels: Channel[], marketplaces: Channel[]): number {
  return 5 + marketplaces.length + channels.length;
}

interface Props {
  product: ProductRow;
  channels: Channel[];
  /**
   * The marketplace subset, which the master row gives a "listed?" column each.
   * Needed here only to emit the matching empty cells — a channel row that
   * skipped them would pull every column right of it out of alignment.
   */
  marketplaces: Channel[];
  canEdit: boolean;
  /** Keys are `${productId}:${channelId}`, held by the table — see ProductTable. */
  expandedChannels: Set<string>;
  onToggleChannel: (key: string) => void;
  onEdited: () => void;
  onToast: (toast: { kind: 'ok' | 'error' | 'warn'; text: string }) => void;
  /**
   * Hands a proposed change up to the confirm dialog rather than pushing it
   * straight to the marketplace. Title edits used to save on blur, which meant
   * clicking away from a half-typed title published it.
   */
  onRequestWrite: (
    listing: Listing,
    field: 'price' | 'title' | 'sku',
    newValue: string | number,
  ) => void;
  /** Ctrl/Cmd+Enter — commits the field exactly as onRequestWrite does, then immediately queues every draft this product has. */
  onRequestWriteAndQueue: (
    listing: Listing,
    field: 'price' | 'title' | 'sku',
    newValue: string | number,
  ) => void;
  /** The local, unqueued draft for a listing's field, if any — see ProductTable's DraftEdit. */
  draftFor: (listingId: string, field: 'price' | 'title' | 'sku') => DraftEdit | undefined;
  /** The price-edit state the whole table shares, passed through to PriceCell. */
  priceEdit: {
    referenceName: string;
    editingKey: string | null;
    draft: string;
    setDraft: (value: string) => void;
    onBeginEdit: (listing: Listing, cellKey: string) => void;
    onCommit: (listing: Listing, productTitle: string) => void;
    onCommitAndQueue: (listing: Listing, product: ProductRow) => void;
    onCancel: () => void;
  };
}

export function ChannelDetail({
  product,
  channels,
  marketplaces,
  canEdit,
  expandedChannels,
  onToggleChannel,
  onEdited,
  onToast,
  onRequestWrite,
  onRequestWriteAndQueue,
  draftFor,
  priceEdit,
}: Props) {
  return (
    <>
      {channels.map((channel) => {
        const listing = product.listings.find((l) => l.channelId === channel.id);
        const key = `${product.id}:${channel.id}`;
        return (
          <ChannelRow
            key={channel.id}
            channel={channel}
            channels={channels}
            marketplaces={marketplaces}
            product={product}
            listing={listing}
            isPrimarySite={product.primarySiteChannelId === channel.id}
            canEdit={canEdit}
            expanded={expandedChannels.has(key)}
            onToggle={() => onToggleChannel(key)}
            onEdited={onEdited}
            onToast={onToast}
            onRequestWrite={onRequestWrite}
            onRequestWriteAndQueue={onRequestWriteAndQueue}
            draftFor={draftFor}
            priceEdit={priceEdit}
          />
        );
      })}
    </>
  );
}

interface RowProps {
  channel: Channel;
  channels: Channel[];
  marketplaces: Channel[];
  product: ProductRow;
  listing: Listing | undefined;
  isPrimarySite: boolean;
  canEdit: boolean;
  expanded: boolean;
  onToggle: () => void;
  onEdited: () => void;
  onToast: (toast: { kind: 'ok' | 'error' | 'warn'; text: string }) => void;
  onRequestWrite: (
    listing: Listing,
    field: 'price' | 'title' | 'sku',
    newValue: string | number,
  ) => void;
  onRequestWriteAndQueue: (
    listing: Listing,
    field: 'price' | 'title' | 'sku',
    newValue: string | number,
  ) => void;
  draftFor: (listingId: string, field: 'price' | 'title' | 'sku') => DraftEdit | undefined;
  priceEdit: Props['priceEdit'];
}

function ChannelRow({
  channel,
  channels,
  marketplaces,
  product,
  listing,
  isPrimarySite,
  canEdit,
  expanded,
  onToggle,
  onEdited,
  onToast,
  onRequestWrite,
  onRequestWriteAndQueue,
  draftFor,
  priceEdit,
}: RowProps) {
  const [editingTitle, setEditingTitle] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');
  const [editingSku, setEditingSku] = useState(false);
  const [draftSku, setDraftSku] = useState('');
  // Which fields this channel accepts. Cached across every row on the page.
  const capabilitiesQuery = useQuery({ queryKey: ['capabilities'], queryFn: api.capabilities });
  // Every channel's configured business pricing rule. Cached across every row
  // on the page, the same way capabilities is — this component is mounted
  // once per (product, channel) pair.
  const businessPricingQuery = useQuery({ queryKey: ['business-pricing'], queryFn: api.businessPricing });
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // Every column the master row has, so the detail row below can span them all.
  const fullSpan = tableColumnSpan(channels, marketplaces);

  if (!listing) {
    return (
      <tr className="channel-row">
        <td />
        <td />
        <td />
        <td className="title-cell channel-cell">
          <span className="channel-name">{channel.name}</span>
          <span className="muted small">Not listed on this channel.</span>
        </td>
        <td className="sku" />
        {marketplaces.map((m) => (
          <td key={`listed-${m.id}`} className="listed-col" />
        ))}
        {channels.map((c) => (
          <td key={c.id} className="price-cell" />
        ))}
      </tr>
    );
  }

  // Blanket block — nothing on this listing is writable. A field-specific
  // block (below) is a narrower, more useful claim than this, so it is checked
  // first and only falls back to this generic reason when it has nothing to say.
  const blocked = listing.writeBlockedReason;
  // Not listing.lastWriteStatus === 'failed' — that column is one cache per
  // listing that a dismiss never revisits (see products.ts's hasWriteError
  // for the same fix), so it would keep showing "write failed" on a write
  // someone already acknowledged. unsettledWrites already excludes those.
  const hasWriteError = listing.unsettledWrites.some(
    (w) => w.writeState === 'not_confirmed' || w.writeState === 'reverted_by_channel',
  );
  const capabilities = capabilitiesQuery.data?.[channel.type];
  const canEditSku =
    canEdit && capabilities?.canWriteSku === true && !listing.writeBlockedReason && !listing.skuWriteBlockedReason;
  // Why this listing's SKU cannot be edited, in order of specificity: a reason
  // about this exact listing (a per-variation Etsy SKU, discovered at sync)
  // beats a reason about the channel in general. Only Amazon is left with a
  // channel-level reason now — the two storefronts are writable like everyone
  // else, so a false there just means "nothing is blocking it" further up.
  const skuReason =
    listing.skuWriteBlockedReason ??
    (channel.type === 'amazon'
      ? 'Amazon identifies a listing by its SKU, so it cannot be renamed here.'
      : 'This channel does not allow the SKU to be changed.');

  // The rule that would apply to this listing's *next* price write: its own
  // override, else the channel's configured rule, else mirror — same
  // precedence as effectiveRule (lib/business-price.ts). Shown only when the
  // listing already has a business price on record; a listing with none is
  // never given one, so there is nothing here to configure or preview.
  const channelBusinessRule = businessPricingQuery.data?.rules.find((r) => r.channelId === channel.id)?.rule;
  const effectiveBusinessRule: BusinessPriceRule =
    listing.businessPriceRule ?? channelBusinessRule ?? MIRROR_RETAIL;
  const nextBusinessPriceMinor =
    listing.priceMinor === null ? null : deriveBusinessPrice(listing.priceMinor, effectiveBusinessRule);

  const saveTitle = (andQueue: boolean) => {
    const title = draftTitle.trim();
    setEditingTitle(false);
    if (title === '' && !andQueue) return;
    // Straight to a local draft — the same care a price edit gets before it
    // reaches the queue, since both publish live once staged.
    if (title !== '' && title !== listing.title) {
      if (andQueue) onRequestWriteAndQueue(listing, 'title', title);
      else onRequestWrite(listing, 'title', title);
    } else if (andQueue) {
      // Nothing new here, but Ctrl+Enter still means "queue whatever else
      // this product already has drafted" — see requestWriteAndQueue.
      onRequestWriteAndQueue(listing, 'title', listing.title);
    }
  };

  const saveSku = (andQueue: boolean) => {
    const sku = draftSku.trim();
    setEditingSku(false);
    if (sku === '' && !andQueue) return;
    if (sku !== '' && sku !== listing.sku) {
      if (andQueue) onRequestWriteAndQueue(listing, 'sku', sku);
      else onRequestWrite(listing, 'sku', sku);
    } else if (andQueue) {
      onRequestWriteAndQueue(listing, 'sku', listing.sku ?? '');
    }
  };

  const removeLink = async () => {
    if (!window.confirm(`Unlink this ${channel.name} listing from ${listing.title}?`)) return;
    setSaving(true);
    try {
      await api.unlink(listing.id);
      onToast({ kind: 'ok', text: `Unlinked from ${channel.name}. It is back in the review queue.` });
      onEdited();
    } catch (error) {
      onToast({ kind: 'error', text: error instanceof Error ? error.message : String(error) });
    } finally {
      setSaving(false);
    }
  };

  // Everything below the fold, so the row can say whether opening it is worth it.
  const extras = Object.entries(listing.extraFields).filter(([key]) => key !== 'Category source');

  // Local, unqueued edits for this listing's title/sku — hidden while
  // actively editing, since the input itself is already showing the draft.
  const titleDraft = editingTitle ? undefined : draftFor(listing.id, 'title');
  const skuDraft = editingSku ? undefined : draftFor(listing.id, 'sku');

  return (
    <>
      <tr className="channel-row">
        <td />
        <td>
          <button
            className="expand-btn"
            onClick={onToggle}
            aria-expanded={expanded}
            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${channel.name} details`}
          >
            {expanded ? '▾' : '▸'}
          </button>
        </td>
        <td />
        <td className="title-cell channel-cell">
          <span className="channel-name">{channel.name}</span>
          {isPrimarySite && <span className="badge">primary</span>}
          {listing.linkType && <span className="badge">{listing.linkType} link</span>}
          {hasWriteError && <span className="badge error">write failed</span>}
          {listing.lastWriteStatus === 'verified' && <span className="badge ok">verified</span>}

          {editingTitle ? (
            <input
              autoFocus
              className="channel-title-input"
              value={draftTitle}
              onChange={(e) => setDraftTitle(e.target.value)}
              onBlur={() => saveTitle(false)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) saveTitle(true);
                else if (e.key === 'Enter') saveTitle(false);
                else if (e.key === 'Escape') setEditingTitle(false);
              }}
            />
          ) : (
            <span
              className={canEdit && !blocked ? 'price-value' : 'price-value readonly'}
              title={blocked ? `Not editable: ${blocked}` : undefined}
              onClick={() => {
                if (!canEdit || blocked || saving) return;
                setDraftTitle(listing.title);
                setEditingTitle(true);
              }}
            >
              {titleDraft ? String(titleDraft.value) : listing.title}
              {canEdit && !blocked && <span className="edit-hint" aria-hidden="true"> ✎</span>}
            </span>
          )}
          {/* Edited locally but not yet queued — "Queue Changes" (or
              Ctrl+Enter) is what turns this into a real staged write. */}
          {titleDraft && (
            <div className="draft-note" title="Edited but not yet queued — click Queue Changes to stage it">
              not queued yet
            </div>
          )}
        </td>

        <td className="sku">
          {editingSku ? (
            <input
              autoFocus
              className="channel-sku-input"
              value={draftSku}
              onChange={(e) => setDraftSku(e.target.value)}
              onBlur={() => saveSku(false)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) saveSku(true);
                else if (e.key === 'Enter') saveSku(false);
                else if (e.key === 'Escape') setEditingSku(false);
              }}
            />
          ) : canEditSku ? (
            <>
              <span
                className="price-value"
                title={`Editable on ${channel.name}`}
                onClick={() => {
                  setDraftSku(listing.sku ?? '');
                  setEditingSku(true);
                }}
              >
                {skuDraft ? String(skuDraft.value) : (listing.sku ?? <span className="muted">none</span>)}
                <span className="edit-hint" aria-hidden="true"> ✎</span>
              </span>
              {/* A one-click fix next to the value it fixes. Routes through
                  the same staging path as a manual edit — onRequestWrite's
                  onExisting:'replace' semantics apply here too, so this may
                  overwrite a sheet-staged row, unlike the bulk sweep, which
                  never does. That asymmetry is deliberate: this is a single,
                  deliberate edit, not an unattended one. */}
              {listing.skuMismatch && (
                <button
                  className="link small"
                  title={`Set to ${product.sku}, matching the website`}
                  onClick={() => onRequestWrite(listing, 'sku', product.sku)}
                >
                  use website SKU
                </button>
              )}
              {skuDraft && (
                <div className="draft-note" title="Edited but not yet queued — click Queue Changes to stage it">
                  not queued yet
                </div>
              )}
            </>
          ) : (
            // The reason it cannot be edited is said in the detail row rather
            // than here, where there is no room for a sentence.
            <span title={canEdit ? skuReason : undefined}>
              {listing.sku ?? <span className="muted">none</span>}
            </span>
          )}
        </td>

        {marketplaces.map((m) => (
          <td key={`listed-${m.id}`} className="listed-col" />
        ))}

        {/* The price lands in this channel's own column; the rest stay empty,
            which is what makes a channel row readable against the headings. */}
        {channels.map((c) =>
          c.id === channel.id ? (
            <PriceCell
              key={c.id}
              channel={c}
              product={product}
              referenceName={priceEdit.referenceName}
              canEdit={canEdit}
              // Distinct from the master row's key for this same cell, so
              // editing one does not open an input in both.
              cellKey={`${product.id}:${c.id}:detail`}
              editingKey={priceEdit.editingKey}
              draft={priceEdit.draft}
              setDraft={priceEdit.setDraft}
              onBeginEdit={priceEdit.onBeginEdit}
              onCommit={priceEdit.onCommit}
              onCommitAndQueue={priceEdit.onCommitAndQueue}
              onCancel={priceEdit.onCancel}
              draftFor={draftFor}
            />
          ) : (
            <td key={c.id} className="price-cell" />
          ),
        )}
      </tr>

      {expanded && (
        <tr className="channel-detail-row">
          <td colSpan={fullSpan}>
            {blocked && <div className="alert warn small">{blocked}</div>}
            {hasWriteError && (
              <div className="alert error small">
                {listing.lastWriteError ?? 'The last write to this channel failed, but no reason was recorded.'}
              </div>
            )}

            <dl>
              <dt>Price</dt>
              <dd>{formatPrice(listing.priceMinor, listing.currency)}</dd>

              {listing.businessPriceMinor !== null && (
                <>
                  <dt>Business price</dt>
                  <dd>
                    {formatPrice(listing.businessPriceMinor, listing.currency)}
                    <span className="muted small"> — {businessPriceRuleLabel(effectiveBusinessRule)}</span>
                    {/* Derived, so it only moves on the next price write — saying
                        what that write will set is the difference between this
                        row being information and being a trap. */}
                    {nextBusinessPriceMinor !== null && nextBusinessPriceMinor !== listing.businessPriceMinor && (
                      <div className="muted small">
                        The next price write will set {formatPrice(nextBusinessPriceMinor, listing.currency)}.
                      </div>
                    )}
                  </dd>
                  <dt>Business price rule</dt>
                  <dd>
                    <BusinessPriceRuleControl
                      listing={listing}
                      channelRule={channelBusinessRule ?? MIRROR_RETAIL}
                      canEdit={canEdit}
                      onEdited={onEdited}
                      onToast={onToast}
                    />
                  </dd>
                </>
              )}

              <dt>Category</dt>
              <dd>
                {listing.category ?? <span className="muted">none</span>}
                {/* An inferred category must never be mistaken for data pulled
                    from the channel, so it is labelled wherever it appears. */}
                {listing.extraFields['Category source'] && (
                  <>
                    {' '}
                    <span className="badge inferred" title={listing.extraFields['Category source'] ?? ''}>
                      inferred
                    </span>
                  </>
                )}
              </dd>

              {listing.quantity !== null && (
                <>
                  <dt>Quantity</dt>
                  <dd>{listing.quantity}</dd>
                </>
              )}

              {extras.map(([key, value]) => (
                <div key={key} style={{ display: 'contents' }}>
                  <dt>{key}</dt>
                  <dd>{value ?? '—'}</dd>
                </div>
              ))}

              <dt>Synced</dt>
              <dd className="muted">
                {listing.lastSyncedAt ? new Date(listing.lastSyncedAt).toLocaleString() : 'never'}
              </dd>

              {/* Said plainly rather than left as a field that silently does
                  nothing when clicked in the row above. */}
              {canEdit && !canEditSku && (
                <>
                  <dt>SKU</dt>
                  <dd className="muted">{skuReason}</dd>
                </>
              )}
            </dl>

            <div className="channel-actions">
              {listing.url && (
                <a href={listing.url} target="_blank" rel="noreferrer" className="small">
                  Open listing ↗
                </a>
              )}
              <button className="link small" onClick={() => setShowHistory((v) => !v)}>
                {showHistory ? 'Hide history' : 'History'}
              </button>
              {/* Linking is a judgement call and judgement calls are sometimes
                  wrong. Unlinking touches neither the listing nor the product —
                  it just puts the listing back in the review queue to be
                  matched again. */}
              {canEdit && listing.linkType && (
                <button className="link small danger" disabled={saving} onClick={() => void removeLink()}>
                  Remove link
                </button>
              )}
            </div>

            {showHistory && (
              <History listingId={listing.id} canEdit={canEdit} onReverted={onEdited} onToast={onToast} />
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function History({
  listingId, canEdit, onReverted, onToast,
}: {
  listingId: string;
  canEdit: boolean;
  onReverted: () => void;
  onToast: (toast: { kind: 'ok' | 'error' | 'warn'; text: string }) => void;
}) {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['history', listingId],
    queryFn: () => api.history(listingId),
  });

  if (isLoading) return <p className="small muted">Loading history…</p>;
  if (!data?.length) return <p className="small muted">No changes recorded yet.</p>;

  const revert = async (editLogId: string) => {
    try {
      const outcome = await api.revert(editLogId);
      onToast({ kind: outcome.ok ? 'ok' : 'error', text: outcome.message });
      await refetch();
      onReverted();
    } catch (error) {
      onToast({ kind: 'error', text: error instanceof Error ? error.message : String(error) });
    }
  };

  return (
    <ul className="small" style={{ paddingLeft: 16, marginTop: 8 }}>
      {data.map((entry: any) => (
        <li key={entry.id} style={{ marginBottom: 6 }}>
          <span className="muted">{new Date(entry.createdAt).toLocaleString()}</span>{' '}
          {entry.field}: <span className="muted">{entry.oldValue ?? '—'}</span> → {entry.newValue}{' '}
          <span className={`badge ${entry.status === 'verified' ? 'ok' : entry.status === 'failed' ? 'error' : ''}`}>
            {entry.status}
          </span>
          {entry.user && <span className="muted"> by {entry.user.email}</span>}
          {canEdit && !entry.revertedAt && entry.status === 'verified' && (
            <>
              {' '}
              <button className="link small" onClick={() => void revert(entry.id)}>
                revert
              </button>
            </>
          )}
          {entry.revertedAt && <span className="muted"> (reverted)</span>}
          {entry.field === 'price' && entry.expectedBusinessPriceMinor != null && (
            <div className="muted" style={{ marginTop: 2 }}>
              business price also set to {formatPrice(entry.expectedBusinessPriceMinor, entry.currency ?? 'USD')}
            </div>
          )}
          {entry.status === 'failed' && entry.message && (
            <div className="muted" style={{ marginTop: 2 }}>
              {entry.message}
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * Edits the per-listing override of the channel-wide business pricing rule.
 * Touches only local configuration — saving never writes to the channel; the
 * business price itself changes on the next *price* write, since the
 * derivation has no independent write of its own.
 */
function BusinessPriceRuleControl({
  listing,
  channelRule,
  canEdit,
  onEdited,
  onToast,
}: {
  listing: Listing;
  channelRule: BusinessPriceRule;
  canEdit: boolean;
  onEdited: () => void;
  onToast: (toast: { kind: 'ok' | 'error' | 'warn'; text: string }) => void;
}) {
  const override = listing.businessPriceRule;
  const [editing, setEditing] = useState(false);
  const [mode, setMode] = useState<'channel' | BusinessPriceRule['mode']>(override ? override.mode : 'channel');
  const [percent, setPercent] = useState(override?.mode === 'percent_off' ? String(override.percent) : '10');
  const [amount, setAmount] = useState(
    override?.mode === 'amount_off' ? (override.amountMinor / 100).toFixed(2) : '0.00',
  );
  const [saving, setSaving] = useState(false);

  if (!editing) {
    return (
      <>
        {override ? businessPriceRuleLabel(override) : <span className="muted">follows the channel default</span>}
        {canEdit && (
          <>
            {' '}
            <button className="link small" onClick={() => setEditing(true)}>
              change
            </button>
          </>
        )}
      </>
    );
  }

  const save = async () => {
    let rule: BusinessPriceRule | null;
    if (mode === 'channel') rule = null;
    else if (mode === 'mirror') rule = { mode: 'mirror' };
    else if (mode === 'percent_off') rule = { mode: 'percent_off', percent: Number(percent) };
    else rule = { mode: 'amount_off', amountMinor: Math.round(Number(amount) * 100) };

    setSaving(true);
    try {
      await api.setListingBusinessPriceRule(listing.id, rule);
      onToast({ kind: 'ok', text: 'Business price rule saved.' });
      setEditing(false);
      onEdited();
    } catch (error) {
      onToast({ kind: 'error', text: error instanceof Error ? error.message : String(error) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      <select value={mode} onChange={(e) => setMode(e.target.value as typeof mode)} disabled={saving}>
        <option value="channel">Use channel default ({businessPriceRuleLabel(channelRule)})</option>
        <option value="mirror">Mirror the retail price</option>
        <option value="percent_off">Percent below retail</option>
        <option value="amount_off">Amount below retail</option>
      </select>
      {mode === 'percent_off' && (
        <span>
          <input
            type="number"
            min={0}
            max={90}
            step="0.1"
            value={percent}
            onChange={(e) => setPercent(e.target.value)}
            disabled={saving}
            style={{ width: 60 }}
          />
          %
        </span>
      )}
      {mode === 'amount_off' && (
        <span>
          $
          <input
            type="number"
            min={0}
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            disabled={saving}
            style={{ width: 70 }}
          />
        </span>
      )}
      <button className="small" onClick={() => void save()} disabled={saving}>
        Save
      </button>
      <button className="link small" onClick={() => setEditing(false)} disabled={saving}>
        Cancel
      </button>
    </span>
  );
}
